import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { TextAutoScrollDirective } from '@frontend/sports/common/ui/text-auto-scroll';
import { ValueSliderDirective } from '@frontend/sports/common/ui/value-slider';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { TooltipModule } from '@frontend/sports/tooltip-feature';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxFor } from '@rx-angular/template/for';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { NgxPopperjsModule } from 'ngx-popperjs';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { ItemComponent } from '../common/item/item.component';
import { CrmPromotionContainerModule } from '../crm-promotion-container/crm-promotion-container.module';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { CrmPromotionModule } from '../crm-promotion/crm-promotion.module';
import { ComponentProxyDirective } from '../deferred/component-proxy.directive';
import { DirectivesModule } from '../directives/directives.module';
import { DropdownModule } from '../dropdown/dropdown.module';
import { FavouritesModule } from '../favourites/favourites.module';
import { GridScoreboardModule } from '../grid-scoreboard/grid-scoreboard.module';
import { MatchSimulationComponent } from '../match-simulation/match-simulation.component';
import { NativeAlertsModule } from '../native-alerts/native-alerts.module';
import { NavigationCoreModule } from '../navigation-core/navigation-core.module';
import { NavigationModule } from '../navigation/navigation.module';
import { EventPickComponent } from '../option-pick/event-pick.component';
import { PriceboostCountSignpostComponent } from '../shared-priceboost/Components/priceboost-count-signpost.component';
import { TabBarModule } from '../tab-bar/tab-bar.module';
import { TimerModule } from '../timers/timer.module';
import { VideoStreamButtonComponent } from '../video-stream/video-stream-button/video-stream-button.component';
import { HeaderContentComponent } from './header/header-content.component';
import { BaseballScoreboardBaseRunnerComponent } from './scoreboard/baseball-scoreboard-base-runner.component';
import { BaseballScoreboardLiveComponent } from './scoreboard/baseball-scoreboard-live.component';
import { BaseballScoreboardComponent } from './scoreboard/baseball-scoreboard.component';
import { BestOfComponent } from './scoreboard/best-of/best-of.component';
import { CricketScoreboardComponent } from './scoreboard/cricket-scoreboard.component';
import { DartsScoreboardComponent } from './scoreboard/darts-scoreboard.component';
import { LeaderboardGameScoreboardComponent } from './scoreboard/leaderboard-game-scoreboard.component';
import { PairGameParticipantComponent } from './scoreboard/pair-game-participant.component';
import { PairGameScoreboardComponent } from './scoreboard/pair-game-scoreboard.component';
import { PenaltyShootoutComponent } from './scoreboard/penalty-shootout/penalty-shootout.component';
import { PeriodGameScoreboardLiveComponent } from './scoreboard/period-game-scoreboard-live.component';
import { PeriodGameScoreboardComponent } from './scoreboard/period-game-scoreboard.component';
import { PrematchScoreboardComponent } from './scoreboard/prematch-scoreboard.component';
import { PrematchTournamentScoreboardComponent } from './scoreboard/prematch-tournament-scoreboard.component';
import { ScoreCardComponent } from './scoreboard/score-card.component';
import { ScoreboardMessageListComponent } from './scoreboard/scoreboard-message-list.component';
import { ScoreboardMessageComponent } from './scoreboard/scoreboard-message.component';
import { ScoreboardParticipantComponent } from './scoreboard/scoreboard-participant.component';
import { ScoreboardTimerComponent } from './scoreboard/scoreboard-timer.component';
import { ScoreboardComponent } from './scoreboard/scoreboard.component';
import { SetGameScoreboardComponent } from './scoreboard/set-game-scoreboard.component';
import { SnookerScoreboardComponent } from './scoreboard/snooker-scoreboard.component';
import { SpecialGameScoreboardComponent } from './scoreboard/special-game-scoreboard.component';
import { StatisticsButtonComponent } from './scoreboard/statistics-button.component';
import { TournamentScoreboardComponent } from './scoreboard/tournament-scoreboard.component';
import { OptionGroupPairPipe } from './tree/option-group-pair.pipe';
import { TreeEventNameComponent } from './tree/tree-event-name.component';
import { TreeParticipantComponent } from './tree/tree-participant.component';
import { TreeRaceInfoComponent } from './tree/tree-race-info.component';
import { TreeComponent } from './tree/tree.component';

const directives = [
    HeaderContentComponent,
    BaseballScoreboardComponent,
    BaseballScoreboardLiveComponent,
    DartsScoreboardComponent,
    LeaderboardGameScoreboardComponent,
    PairGameParticipantComponent,
    PairGameScoreboardComponent,
    PeriodGameScoreboardComponent,
    PeriodGameScoreboardLiveComponent,
    ScoreboardComponent,
    ScoreboardMessageComponent,
    ScoreboardMessageListComponent,
    ScoreboardParticipantComponent,
    ScoreboardTimerComponent,
    ScoreCardComponent,
    SetGameScoreboardComponent,
    SnookerScoreboardComponent,
    SpecialGameScoreboardComponent,
    StatisticsButtonComponent,
    TournamentScoreboardComponent,
    PenaltyShootoutComponent,
    BestOfComponent,
    PrematchScoreboardComponent,
    PrematchTournamentScoreboardComponent,
    BaseballScoreboardBaseRunnerComponent,
    CricketScoreboardComponent,
];

@NgModule({
    imports: [
        TooltipModule,
        NavigationCoreModule,
        NavigationModule,
        DropdownModule,
        ComponentProxyDirective,
        DirectivesModule,
        CrmPromotionContainerModule,
        CrmPromotionModule,
        CommonModule,
        CompetitionLogoComponent,
        FavouritesModule,
        NativeAlertsModule,
        TabBarModule,
        TimerModule,
        ParticipantImageComponent,
        NgScrollbarModule,
        AutomationModule,
        GridScoreboardModule,
        NgxPopperjsModule,
        DynamicComponent,
        DynamicIoDirective,
        ShowInEpcotDirective,
        ItemComponent,
        TextAutoScrollDirective,
        ValueSliderDirective,
        ScrollAdapterComponent,
        BadgeComponent,
        EventPickComponent,
        RxFor,
        OptionGroupPairPipe,
        TreeParticipantComponent,
        TreeRaceInfoComponent,
        TreeEventNameComponent,
        MatchSimulationComponent,
        VideoStreamButtonComponent,
        PriceboostCountSignpostComponent,
        IconCustomComponent,
        DsButtonIcon,
        NgOptimizedImage,
        IconCustomComponent,
        NativePriceFormatterPipe,
        TreeComponent,
    ],
    declarations: directives,
    exports: [...directives, TreeComponent],
})
export class EventDetailsCommonModule {}
